import {
  Booster,
  CardStatus,
  Contest,
  ContestParticipant,
  ContestResponse,
  DailySpin,
  DailySpinStatus,
  PaginationResponse,
  Player,
  Prize,
  User,
} from 'models';

import { api } from './api';

const playerApiUrl = process.env.REACT_APP_PLAYER_API_URL || 'https://clutchpoints-middlepoint.4taps.me';

const contestApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getContest: builder.query<ContestResponse, void>({
      query: () => {
        return {
          url: `contest/`,
        };
      },
    }),
    getContestsByDate: builder.query<Contest[], { date: string; league?: string }>({
      query: ({ date, league }) => {
        const params = league ? `?league=${league}` : '';
        return {
          url: `contest/date/${date}/${params}`,
        };
      },
      providesTags: (result, error, { date }) => (result ? [{ type: 'ContestsByDate', id: date }] : []),
    }),
    getContestParticipant: builder.query<ContestParticipant, number>({
      query: (id) => {
        return {
          url: `contest/participant/`,
          params: {
            contestId: id,
          },
        };
      },
      providesTags: (result, error, id) => [{ type: 'ContestParticipant', id }],
    }),

    getSpin: builder.query<DailySpin, void>({
      query: () => {
        return {
          url: `contest/spin/`,
        };
      },
    }),

    createContestParticipant: builder.mutation<ContestParticipant, number>({
      query: (contestId) => ({
        url: `contest/participant/`,
        method: 'POST',
        body: {
          contestId,
        },
        headers: {
          accept: 'application/json',
        },
      }),
    }),
    chooseCard: builder.mutation<ContestParticipant, { contestId: number; cardId: number; status: CardStatus }>({
      query: (body) => ({
        url: `contest/participant/card/`,
        method: 'POST',
        body: body,
        headers: {
          accept: 'application/json',
        },
      }),
    }),

    getPlayerByID: builder.query<Player, string>({
      query: (id: string) => {
        return {
          url: `${playerApiUrl}/api/front/players/${id}/`,
        };
      },
    }),

    getBoosters: builder.query<Booster[], void>({
      query: () => {
        return {
          url: `contest/booster/`,
        };
      },
    }),
    getParticipantBoosters: builder.query<Booster[], { league?: string } | void>({
      query: (params) => {
        const league = params && 'league' in params ? params.league : undefined;
        const queryParams = league && league !== 'all' ? `?league=${league}` : '';
        return {
          url: `contest/participant/booster/${queryParams}`,
        };
      },
    }),

    checkSpin: builder.query<DailySpinStatus, void>({
      query: () => {
        return {
          url: `contest/spin/check/`,
        };
      },
    }),

    setParticipantBooster: builder.mutation<ContestParticipant, { participantId: number; boosterId: number }>({
      query: (body) => ({
        url: `contest/participant/booster/`,
        method: 'POST',
        body: body,
        headers: {
          accept: 'application/json',
        },
      }),
    }),
    completeParticipant: builder.mutation<ContestParticipant, { participantId: number }>({
      query: (body) => ({
        url: `contest/participant/complete/`,
        method: 'POST',
        body: body,
        headers: {
          accept: 'application/json',
        },
      }),
    }),
    inviteUser: builder.mutation({
      query: ({ code }) => ({
        url: `contest/friend/invite/`,
        method: 'POST',
        body: {
          code: code,
        },
        headers: {
          accept: 'application/json',
        },
      }),
    }),
    getParticipantHistory: builder.query<
      PaginationResponse<ContestParticipant>,
      { limit?: number; offset?: number; userId: string | number }
    >({
      query: ({ limit = 20, offset = 0, userId }) => ({
        url: `contest/participant/history/?limit=${limit}&offset=${offset}&user_id=${userId}`,
      }),
      providesTags: (result, error, { userId }) => (result ? [{ type: 'History', id: userId }] : []),
    }),
    createFakeUser: builder.mutation<{ token: string }, void>({
      query: () => {
        const currentDate = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
        return {
          url: `contest/participant/fake/`,
          method: 'POST',
          headers: {
            accept: 'application/json',
          },
          body: {
            date: currentDate,
          },
        };
      },
    }),
    resetContest: builder.mutation<ContestParticipant, { id: number }>({
      query: ({ id }) => ({
        url: `contest/participant/card/reroll/`,
        method: 'POST',
        headers: {
          accept: 'application/json',
        },
        body: {
          contestId: id,
        },
      }),
    }),
    getPrizes: builder.query<PaginationResponse<Prize>, { limit?: number; offset?: number }>({
      query: ({ limit = 20, offset = 0 }) => ({
        url: `contest/prize/`,
        params: {
          limit,
          offset,
        },
      }),
    }),
    duplicateContest: builder.mutation<Contest, { contestId: number }>({
      query: ({ contestId }) => ({
        url: `contest/fake/`,
        method: 'POST',
        body: { contestId },
      }),
    }),
  }),
});

export const {
  useGetContestQuery,
  useLazyGetContestQuery,
  useGetContestsByDateQuery,
  useLazyGetContestsByDateQuery,
  useGetContestParticipantQuery,
  useLazyGetContestParticipantQuery,
  useCreateContestParticipantMutation,
  useGetSpinQuery,
  useLazyGetSpinQuery,
  useGetPlayerByIDQuery,
  useLazyGetPlayerByIDQuery,
  useChooseCardMutation,
  useGetBoostersQuery,
  useLazyGetBoostersQuery,
  useCheckSpinQuery,
  useLazyCheckSpinQuery,
  useGetParticipantBoostersQuery,
  useLazyGetParticipantBoostersQuery,
  useSetParticipantBoosterMutation,
  useCompleteParticipantMutation,
  useInviteUserMutation,
  useGetParticipantHistoryQuery,
  useLazyGetParticipantHistoryQuery,
  useCreateFakeUserMutation,
  useResetContestMutation,
  useGetPrizesQuery,
  useLazyGetPrizesQuery,
  useDuplicateContestMutation,
} = contestApi;
